import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, CalendarPlus, Calendar, Clock, MapPin, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });
const emptyForm = () => ({ title: "", date: "", time: "", location: "", description: "" });
const fieldClass = "h-11 rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60";
const fmtDate = (d) => { if (!d) return ""; try { return new Date(d + "T00:00:00").toLocaleDateString("de-CH", { weekday: "short", day: "2-digit", month: "long", year: "numeric" }); } catch { return d; } };

const EventsPanel = ({ onLogout }) => {
  const [events, setEvents] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/events`, { headers: authHeader() });
      setEvents(data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Events konnten nicht geladen werden.");
      setEvents([]);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/events`, {
        title: form.title.trim(),
        date: form.date || null,
        time: form.time || null,
        location: form.location.trim() || null,
        description: form.description.trim() || null,
      }, { headers: authHeader() });
      setEvents((prev) => [...prev, data]);
      toast.success("Event erstellt.");
      setDialogOpen(false);
      setForm(emptyForm());
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/events/${id}`, { headers: authHeader() });
      setEvents((prev) => prev.filter((ev) => ev.id !== id));
      toast.success("Event gelöscht.");
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  };

  if (events === null) return <div className="flex items-center justify-center py-24 text-cream/60"><Loader2 size={28} className="animate-spin" /></div>;

  return (
    <div data-testid="events-panel">
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="section-eyebrow">Termine</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Events</h1>
          <p className="mt-2 text-sm text-cream/60">Events erscheinen für alle Mitglieder im Portal.</p>
        </div>
        <button onClick={() => { setForm(emptyForm()); setDialogOpen(true); }} data-testid="events-add" className="inline-flex items-center gap-2 rounded-full bg-gold hover:bg-gold-soft text-anthracite px-5 py-2.5 text-sm font-semibold transition-colors">
          <Plus size={16} /> Event erstellen
        </button>
      </div>

      {events.length === 0 ? (
        <p className="text-sm text-cream/45 py-12 text-center border border-dashed border-white/10 rounded-2xl" data-testid="events-empty">
          Noch keine Events. Erstelle das erste Event oben rechts.
        </p>
      ) : (
        <div className="grid gap-5 md:grid-cols-2" data-testid="events-list">
          {events.map((ev) => (
            <article key={ev.id} data-testid={`event-card-${ev.id}`} className="rounded-2xl border border-gold/15 bg-white/[0.03] p-6">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-semibold text-cream text-lg">{ev.title}</h3>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`event-delete-${ev.id}`} disabled={deletingId === ev.id} aria-label="Löschen" className="text-cream/40 hover:text-red-400 transition-colors disabled:opacity-50">
                      {deletingId === ev.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-cream">Event löschen?</AlertDialogTitle>
                      <AlertDialogDescription className="text-cream/60">„{ev.title}" wirklich löschen?</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                      <AlertDialogAction data-testid={`event-delete-confirm-${ev.id}`} onClick={() => remove(ev.id)} className="bg-red-600 text-white hover:bg-red-700">Löschen</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-cream/60">
                {ev.date && <span className="inline-flex items-center gap-1.5"><Calendar size={13} style={{ color: "#E5C76B" }} /> {fmtDate(ev.date)}</span>}
                {ev.time && <span className="inline-flex items-center gap-1.5"><Clock size={13} style={{ color: "#E5C76B" }} /> {ev.time}</span>}
                {ev.location && <span className="inline-flex items-center gap-1.5"><MapPin size={13} style={{ color: "#E5C76B" }} /> {ev.location}</span>}
              </div>
              {ev.description && <p className="mt-3 text-sm text-cream/80 whitespace-pre-wrap border-t border-cream/10 pt-3">{ev.description}</p>}

              <div className="mt-4 grid grid-cols-2 gap-3 border-t border-cream/10 pt-4" data-testid={`event-rsvp-${ev.id}`}>
                <div data-testid={`event-going-${ev.id}`}>
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] mb-2" style={{ color: "#E5C76B" }}>
                    <Check size={13} /> Kommen ({(ev.going || []).length})
                  </p>
                  {(ev.going || []).length === 0 ? (
                    <p className="text-xs text-cream/35">Noch niemand.</p>
                  ) : (
                    <ul className="space-y-1">
                      {ev.going.map((p) => (
                        <li key={p.user_id} className="text-sm text-cream/85 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                          <span className="truncate">{p.name}</span>
                          <span className="text-[10px] text-cream/40">{p.rank}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="border-l border-cream/10 pl-3" data-testid={`event-declined-${ev.id}`}>
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] mb-2 text-cream/45">
                    <X size={13} /> Kommen nicht ({(ev.declined || []).length})
                  </p>
                  {(ev.declined || []).length === 0 ? (
                    <p className="text-xs text-cream/35">Noch niemand.</p>
                  ) : (
                    <ul className="space-y-1">
                      {ev.declined.map((p) => (
                        <li key={p.user_id} className="text-sm text-cream/55 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-cream/30 flex-shrink-0" />
                          <span className="truncate">{p.name}</span>
                          <span className="text-[10px] text-cream/35">{p.rank}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-anthracite-soft border border-gold/20 text-cream sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cream flex items-center gap-2"><CalendarPlus size={18} style={{ color: "#E5C76B" }} /> Event erstellen</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="event-form">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Titel</span>
              <Input value={form.title} onChange={update("title")} required data-testid="event-form-title" className={fieldClass} />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Datum</span>
                <Input type="date" value={form.date} onChange={update("date")} data-testid="event-form-date" className={`${fieldClass} [color-scheme:dark]`} />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Uhrzeit</span>
                <Input type="time" value={form.time} onChange={update("time")} data-testid="event-form-time" className={`${fieldClass} [color-scheme:dark]`} />
              </label>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Ort</span>
              <Input value={form.location} onChange={update("location")} data-testid="event-form-location" className={fieldClass} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Beschreibung</span>
              <Textarea value={form.description} onChange={update("description")} rows={3} data-testid="event-form-description" className="rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60 resize-none" />
            </label>
            <DialogFooter className="mt-2">
              <button type="submit" disabled={saving} data-testid="event-form-submit" className="btn-primary w-full disabled:opacity-70">
                {saving ? <><Loader2 size={16} className="animate-spin" /> Speichern…</> : "Event erstellen"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsPanel;
